// The circle (SPEC-redesign §5.2, §5.6, §6; SPEC-viewfinder §1): a viewfinder
// that never moves, with labelled distance rings, over a city you drag under
// it. Whatever is under the centre is „stąd"; the zoom IS the radius, so a
// pinch, the wheel and the slider all set the same one number.
//
// Since Decision 47 the map is the spatial control. The camera is owned by
// `./camera.ts` while a gesture is in flight and by `camera()` below at every
// other moment; the two never write it at once (ADR-0018).
//
// Since Decision 46 the basemap fills the block and the radius is a ring drawn
// on it, with the map outside that ring washed back to paper. The strip, the
// attribution, the crosshair and the dial's line are chrome ON the map: opaque
// paper, above every mark, and hit-testable.
import { Map as MlMap, setWorkerUrl } from 'maplibre-gl';
// MapLibre builds its worker's URL at runtime — `new URL(`./${name}`, import.meta.url)`
// — which no bundler can follow, so the chunk is never emitted and the request
// falls through to index.html. The worker then fails to parse and closes, and
// because MapLibre fetches vector tiles *from the worker*, the basemap loads its
// style, loads its TileJSON and then asks for no tile at all, for ever: no error
// event, no failed request, nothing for `data-map` to report.
//
// `?worker&url` bundles the worker with its own imports and hands back its
// hashed same-origin path, which is what `worker-src 'self'` allows (§6.4).
// Plain `?url` is not enough: it copies the file verbatim and the worker's own
// `./maplibre-gl-shared.mjs` then 404s, failing in exactly the same silent way.
import workerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url';

import { derived, type DerivedState } from '../derived.ts';
import { expandCircle } from '../kreska/kreska.ts';
import { distanceLabel } from '../shared/geo.ts';
import type { Mark } from '../shared/model.ts';
import { DIAL_UNDER, markReady, mount } from '../shell.ts';
import { resolveCentre } from '../state/centre.ts';
import { located, returnToNow } from '../state/commands.ts';
import { movedByHand } from '../state/moved.ts';
import type { ViewState } from '../state/viewstate.ts';
import { viewState } from '../state/viewstate.ts';
import { theme } from '../theme.ts';
import type { ThemeName } from '../tokens.ts';
import { paintBeyond, translateBeyond } from './beyond.ts';
import { type CameraOwner, ownCamera } from './camera.ts';
import { dialText as dialLabel } from './dial.ts';
import { solveDistricts, translateDistricts } from './districts-layer.ts';
import { hearGesture } from './live-marks.ts';
import { type Badge, crosshair, dotElement, markButton, mergeBadges } from './marks-dom.ts';
import { markContext, marksFor } from './marks.ts';
import { clampMarks, drawSpoke, measureMarks, paintRings } from './place.ts';
import {
  type Anchor,
  clusterAnchors,
  radiusForSlider,
  SLIDER_STEPS,
  sliderForRadius,
  zoomFor,
} from './wheel-geometry.ts';
import { applyRadiusBands } from './wheel-style.ts';

setWorkerUrl(workerUrl);

/** Our own Papier style, generated from src/tokens.ts (§6.2). */
const styleUrl = (name: ThemeName): string => `/papier-${name}.json`;

/** A centre move eases over 350 ms, as the prototype does (app.js:157). */
const EASE_MS = 350;

const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * The standing derivation, from `src/derived.ts` — the marks this wheel draws,
 * built once per change from one clock. Set by `mountWheel`'s subscription and
 * read by `paintMarks`, which also runs for a resize or a style reload, when
 * nothing has changed for the derivation to be rebuilt from.
 */
let latest: DerivedState = derived.get();

export function mountWheel(): void {
  const section = mount('wheel');

  // The basemap fills the block (Decision 46): no circular mask, so the city
  // beyond the radius is drawn at the same scale as the city within it.
  const box = document.createElement('div');
  box.className = 'circle__map';
  section.append(box);

  // Outside the radius the map is context, not reach: a layer of paper over the
  // tiles with a hole exactly `--disc` across. Purely visual, and takes no
  // pointer — it sits between the tiles and everything drawn on them.
  const wash = document.createElement('div');
  wash.className = 'circle__wash';
  wash.setAttribute('aria-hidden', 'true');
  section.append(wash);

  // The rings, the spoke and the marks ride above the tiles, and take no
  // pointer: the drag, the pinch and the wheel belong to the map (§1.2).
  const overlay = document.createElement('div');
  overlay.className = 'circle__overlay';
  // Beyond the ring (§3): the FIRST child of the overlay, so every ring, name,
  // mark, tag, badge and piece of chrome is painted over it, and above the wash
  // because the dots are drawn ON the washed map (D-46 as §3 amends it).
  const beyondLayer = document.createElement('div');
  beyondLayer.className = 'circle__beyonds';
  beyondLayer.setAttribute('aria-hidden', 'true');
  // The radius itself, and the one box in the block that is `--disc` across:
  // the zoom, and both zoom stops, are derived from it.
  const radiusRing = document.createElement('div');
  radiusRing.className = 'circle__radius-ring';
  radiusRing.setAttribute('aria-hidden', 'true');
  const ringLayer = document.createElement('div');
  ringLayer.className = 'circle__rings';
  const spoke = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  spoke.setAttribute('class', 'circle__spoke');
  spoke.setAttribute('aria-hidden', 'true');
  const spokeLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  spoke.append(spokeLine);
  // The city's own names, drawn by us because the basemap's ran off the block
  // and `within` does not hold a point label inside a polygon (§6.2). They are
  // scenery: no name, no tab stop, and a mark always wins the space.
  const districtLayer = document.createElement('div');
  districtLayer.className = 'circle__districts';
  districtLayer.setAttribute('aria-hidden', 'true');

  const markLayer = document.createElement('div');
  markLayer.className = 'circle__marks';

  // The centre of the circle, always drawn: a 8 px dot by default and a sharp
  // 12 px square once it has been moved, both rotated 45° — verbatim from the
  // prototype's own markup (`centreSize` / `centreRadius`).
  const centreMark = document.createElement('div');
  centreMark.className = 'circle__centre';
  centreMark.setAttribute('aria-hidden', 'true');

  overlay.append(beyondLayer, radiusRing, ringLayer, districtLayer, spoke, centreMark, markLayer);
  section.append(overlay);

  // A licence obligation, not decoration: no state hides it (§6.2, Decision 46).
  const attribution = document.createElement('footer');
  attribution.className = 'circle__attribution mono';
  // `contentinfo` is the PAGE's footer and a page has one, so claiming it here
  // made two landmarks of that name (WCAG 1.3.1). `note` is what this is —
  // ancillary content beside the map — and it is a role `aria-label` may name.
  // Dropping the role leaves one `contentinfo` too, but where <footer> does not
  // map to `sectionfooter` the element falls back to `generic`, where naming is
  // prohibited and „Źródła mapy" (§7.1) would be lost. Written down, not hoped for.
  attribution.setAttribute('role', 'note');
  attribution.setAttribute('aria-label', 'Źródła mapy');
  attribution.textContent = '© OpenStreetMap · OpenMapTiles · OpenFreeMap';
  section.append(attribution);

  // The 56 px under the disc (§5.2): `⟷`, the slider, the value in mono.
  const strip = document.createElement('div');
  strip.className = 'circle__strip';
  const stripGlyph = document.createElement('span');
  stripGlyph.className = 'circle__strip-glyph';
  stripGlyph.setAttribute('aria-hidden', 'true');
  stripGlyph.textContent = '⟷';
  const slider = document.createElement('input');
  slider.type = 'range';
  slider.className = 'circle__radius';
  slider.min = '0';
  slider.max = String(SLIDER_STEPS);
  slider.step = '1';
  slider.setAttribute('aria-label', 'Promień');
  const value = document.createElement('span');
  value.className = 'circle__strip-value mono';
  strip.append(stripGlyph, slider, value);
  section.append(strip);

  // Only once the centre has been moved, and it returns to „ja, teraz" in one
  // tap (§3). Its name is the prototype's, read out of the running page.
  const recentre = document.createElement('button');
  recentre.type = 'button';
  recentre.className = 'circle__recentre';
  recentre.setAttribute('aria-label', 'Wróć do: ja, teraz');
  recentre.title = 'ja, teraz';
  recentre.hidden = true;
  recentre.append(crosshair());
  recentre.addEventListener('click', () => {
    // The same command the kreska's `◇` calls, so the two cannot disagree about
    // where „ja, teraz" is or about what Back does afterwards (#226).
    returnToNow(located());
  });
  section.append(recentre);

  // The dial's own text (§10.13, D-9). Tapping it returns to the full wheel.
  const dialText = document.createElement('button');
  dialText.type = 'button';
  dialText.className = 'circle__dial-text';
  dialText.addEventListener('click', () => {
    // S3's, and the only line of theirs this module calls. `memory.grip` has no
    // change notification, so writing it from here reached nothing of theirs
    // and the tap did nothing; one writer also keeps the remembered proportion
    // from drifting between the grip, the keyboard and this.
    expandCircle();
  });

  // §6.5, D-3: one line of copy under a disc that stayed paper. It is a
  // `status`, so a resident who cannot see the hatch is told as well.
  const failed = document.createElement('p');
  failed.className = 'circle__failed';
  failed.setAttribute('role', 'status');
  failed.hidden = true;
  failed.textContent = 'Mapa się nie wczytała. Odległości i czasy działają dalej.';
  section.append(dialText, failed);

  /** The disc's pixel diameter, measured — `--disc` is a CSS value S3 moves. */
  let disc = 0;
  /** The drawn mark the spoke points at, so a camera move can follow it. */
  let selectedEl: HTMLElement | null = null;
  /** What the last full paint settled on, so a camera move can re-place from it. */
  let placed: { view: ViewState; centre: { lat: number; lon: number }; dial: boolean } | null = null;
  /** A frame's worth of `move` events costs one re-projection, not sixty. */
  let reprojectQueued = 0;
  const scheduleReproject = () => {
    if (reprojectQueued) return;
    reprojectQueued = requestAnimationFrame(() => {
      reprojectQueued = 0;
      reproject();
    });
  };
  const startCentre = resolveCentre(viewState.get());

  const map = new MlMap({
    container: box,
    style: styleUrl(theme.resolved()),
    center: [startCentre.lon, startCentre.lat],
    // A real diameter is not known until the box is laid out; the observer
    // below re-derives the zoom from the measured one before anything is drawn.
    zoom: 13.2,
    // The map IS the spatial control (Decision 47, SPEC-viewfinder §1.2). Which
    // handlers are on is `./camera.ts`'s answer, re-asked whenever the disc,
    // the dial or the basemap's fate changes — the dial and a failed basemap
    // offer no gesture at all (§6), so nothing is enabled here.
    interactive: true,
    dragPan: false,
    scrollZoom: false,
    touchZoomRotate: false,
    doubleClickZoom: false,
    dragRotate: false,
    pitchWithRotate: false,
    boxZoom: false,
    keyboard: false,
    attributionControl: false,
    fadeDuration: 0,
    // MapLibre names its canvas „Map"; this app's map is of one city (§7.1).
    locale: { 'Map.Title': 'Mapa Poznania' },
  });

  /** A basemap that never loaded offers no gesture: there is no camera to drag (§6). */
  let mapFailed = false;

  // The gesture owner (SPEC-viewfinder §1.1, ADR-0018). Built before anything
  // can paint, so the suppression flag exists on every path that reaches
  // `camera()`. It hears MapLibre's own events; nothing here hears a pointer.
  const gestures: CameraOwner = ownCamera({
    map,
    block: section,
    marks: markLayer,
    disc: () => disc,
    // §6, ruled: no gestures in the dial — 72 px is not a viewfinder — and none
    // with a basemap that never loaded, where the slider and the list go on.
    offered: () => !isDial() && !mapFailed,
    reduced: reducedMotion,
    // The per-frame path, and it never goes through `derived.subscribe`: the
    // slider and the bands follow the finger while `viewState` hears nothing
    // until the camera is at rest (§1.1, rule 2).
    onFrame: paintRadius,
  });

  // Either way: a basemap that never loads must not hang `data-ready`, or the
  // map-failed scenario could not be tested at all (§6.5, D-3). The shell keeps
  // the first answer and owns `html[data-map]`, so saying it twice is harmless.
  map.on('load', () => {
    markReady('map', 'ok');
    gestures.refresh();
    paint();
  });
  map.on('error', () => {
    markReady('map', 'failed');
    mapFailed = true;
    failed.hidden = false;
    gestures.refresh();
    // The rings, the marks and the rest of the screen keep working (D-3).
    paint();
  });
  // The camera moves on its own for 350 ms after a „Stąd", and every frame of
  // a gesture. Without this the basemap slides out from under stationary marks
  // and they jump at whatever repaints next — measured at 98 px, exactly the
  // camera delta.
  map.on('move', scheduleReproject);
  // `move` stops one frame short of the final camera on some paths, so the
  // settled position is written once more when the movement ends.
  map.on('moveend', reproject);

  // `setStyle` rebuilds every layer, so the radius bands have to be put back —
  // on `style.load`, which fires once per style, and never on `styledata`,
  // which is also what applying them fires.
  map.on('style.load', () => {
    applyRadiusBands(map, viewState.get().radius);
    paint();
  });

  // The dial is a fact about the block's height, and the block eases over
  // 0,18 s when the grip moves it. `chrome()` reads that height synchronously,
  // so a paint fired by the grip's own change reads the height the block is
  // LEAVING: the class lagged a whole gesture behind — the disc stayed 72 px
  // after the block had grown to 148 — until some unrelated repaint caught up.
  // The section is observed for that reason, and the paint fires only when the
  // boolean flips, never on every frame of the transition.
  let wasDial = isDial();
  new ResizeObserver(() => {
    const now = isDial();
    if (now === wasDial) return;
    wasDial = now;
    // The dial is one of the two states that offer no gesture (§6), so the
    // handler set is re-asked the moment the boolean flips.
    gestures.refresh();
    paint();
  }).observe(section);

  // `--disc` is a CSS value — `min(700px, 100dvh − 152px, 100dvw − 520px)` on
  // the desktop, bound by the window's width as well as its height — so it is
  // measured, never parsed. A ResizeObserver means S3's grip re-derives the
  // zoom for free, and fitness function 3 keeps `getComputedStyle` out.
  //
  // The radius ring is measured rather than the map box, because a failed
  // basemap takes that box out of the layout entirely (§6.5) — and the rings,
  // the marks and the radius have to go on working exactly then (D-3).
  new ResizeObserver(() => {
    const width = radiusRing.getBoundingClientRect().width;
    if (width === disc || width === 0) return;
    disc = width;
    // The zoom stops ARE the ends of the radius range and both are derived from
    // the measured disc, so they move with it (§1.2).
    gestures.refresh();
    paint();
  }).observe(radiusRing);

  // Since Decision 46 those are two different boxes: the grip's range takes the
  // block past 684 px while `--disc` is capped at 342, so the map grows while
  // the radius stands still. Watching only the ring left MapLibre with a stale
  // canvas for the whole of that range; watching only the map re-derived the
  // zoom from the wrong number.
  let mapBox = '';
  new ResizeObserver(() => {
    const rect = overlay.getBoundingClientRect();
    const now = `${rect.width}x${rect.height}`;
    if (now === mapBox || rect.width === 0) return;
    mapBox = now;
    // A resize moves the camera, and a camera move nobody asked for is a
    // gesture to the owner — so this one says who asked (ADR-0018).
    gestures.own(() => map.resize());
    paint();
  }).observe(overlay);

  // A slider is not an opening, so it writes no history entry (§4.2, §6.3).
  slider.addEventListener('input', () => {
    viewState.set({ radius: radiusForSlider(Number(slider.value)) }, 'replace');
  });

  // One gesture, one result (D-d): a drag that moved the camera must not also
  // select whatever mark the finger happened to be over when it lifted. The
  // suppression is a flag cleared by the next `pointerdown` and NEVER a timer —
  // a swallow window eats the tap that follows it, which is the bug the long
  // press hit and fixed the same way before it was removed (§1.4).
  overlay.addEventListener(
    'click',
    (e) => {
      if (!gestures.dragged()) return;
      e.stopPropagation();
      e.preventDefault();
    },
    true,
  );

  // `theme.subscribe` calls back synchronously on subscribe, and a `setStyle`
  // there would fetch a second style at boot — which `stubApp` counts (§9.2).
  let painted: ThemeName = theme.resolved();
  theme.subscribe((resolved) => {
    if (resolved === painted) return;
    painted = resolved;
    map.setStyle(styleUrl(resolved));
  });

  // The one subscription that draws this wheel. It stands where three used to —
  // the view, the snapshot and the centre, the last of which moves when the
  // browser finally answers with a position and no view change announces it
  // (§5.8). One change now reaches here once: a `tu=` change painted the wheel
  // twice before, because the view and the centre both announced it (ADR-0016).
  // Subscribing calls back at once with what is standing, so this is also the
  // first paint.
  derived.subscribe((state) => {
    latest = state;
    paint();
  });

  // ---- the parts that need the closure ----

  /**
   * Whether the block has shrunk to the dial (§5.3, D-9). The threshold is
   * `src/shell.ts`'s, shared with the kreska's own `geometry`; this one
   * measures the block that IS, not the one the grip is about to set.
   */
  function isDial() {
    return section.getBoundingClientRect().height < DIAL_UNDER;
  }

  /**
   * One change, one drawing: the camera first, then everything else. Every
   * caller of this wanted both — and the two are separable precisely because
   * ONE of them is what a gesture takes away (ADR-0018).
   */
  function paint() {
    camera();
    chrome();
  }

  /**
   * **The only writer of the camera outside a gesture, and the only thing a
   * gesture suppresses** (SPEC-viewfinder §1.1, ADR-0018).
   *
   * The suppression is one early return, here rather than at the seven call
   * sites, and the flag it reads lives in `./camera.ts` — on the wheel's side
   * of the seam. `src/derived.ts` knows nothing of a camera and stays neutral
   * under a gesture (§1.1, rule 1).
   *
   * Everything the state→camera path ever served still works: a pasted link,
   * Back, `◌`, the slider, a `hashchange`. What no longer happens is this
   * function writing the camera back from under a finger that is moving it.
   */
  function camera() {
    if (disc === 0 || gestures.owns()) return;
    const view = viewState.get();
    const centre = resolveCentre(view);
    // Zoom is derived so the disc's diameter is exactly twice the radius (§6.3).
    const zoom = zoomFor(view.radius, disc, centre.lat);
    const at = map.getCenter();
    // The prototype's own tolerances (app.js:156): a repaint happens on every
    // data poll, and a camera written on each of them restarts the move for
    // nothing — which is enough to keep the basemap permanently loading.
    const moved = Math.abs(at.lat - centre.lat) > 1e-6 || Math.abs(at.lng - centre.lon) > 1e-6;
    if (!moved && Math.abs(map.getZoom() - zoom) <= 0.01) return;
    // Reduced motion jumps, and so does a zoom-only change: the prototype
    // does, and a slider that eased would lag the finger at every step.
    gestures.own(() => {
      if (moved && !reducedMotion()) {
        map.easeTo({ center: [centre.lon, centre.lat], zoom, duration: EASE_MS });
      } else {
        map.jumpTo({ center: [centre.lon, centre.lat], zoom });
      }
    });
  }

  /**
   * The radius as the strip and the basemap show it. Split out of {@link chrome}
   * because it is the ONE piece a gesture needs per frame: nothing writes
   * `viewState` under a finger, so `derived` never pushes, and without this the
   * slider would sit still through a whole pinch (§1.1).
   */
  function paintRadius(radius: number) {
    applyRadiusBands(map, radius);
    // The slider follows a pasted link as well as the finger.
    const position = String(sliderForRadius(radius));
    if (slider.value !== position) slider.value = position;
    const label = distanceLabel(radius);
    value.textContent = label;
    slider.setAttribute('aria-valuetext', label);
  }

  /**
   * Everything in the block that is not the camera. Driven by
   * `derived.subscribe` and run on every change, gesture or not: a radius
   * changed by a pinch must still move the slider and re-band the map under the
   * finger (§1.1).
   */
  function chrome() {
    if (disc === 0) return;
    const view = viewState.get();
    const centre = resolveCentre(view);
    paintRadius(view.radius);

    // Only once the centre has actually been moved; „ja" is not a move (#226).
    recentre.hidden = !movedByHand(view);

    const dial = isDial();
    section.classList.toggle('circle--dial', dial);

    // A moved centre is a sharp square, „ja, teraz" a round dot (the prototype's
    // `centreSize` / `centreRadius`). The kreska says the same thing in words.
    centreMark.classList.toggle('is-moved', movedByHand(view));

    // „Tarcza: moje linie 16, 99, promień 3,0 km" (§10.13).
    dialText.textContent = dial ? dialLabel(view) : '';

    placed = { view, centre, dial };
    paintRings(ringLayer, disc, view, dial);
    paintMarks(view, dial);
    // The dots the toolbar counts as „poza M", and the same array (§3).
    paintBeyond(beyondLayer, dial ? [] : (latest.derived?.beyond ?? []), project);
    // Solved here and only here, and merely translated between settles (§4.3).
    solveDistricts(districts, view, centre, dial);
  }

  /** Where a lon/lat lands in the overlay's pixels. The camera's answer, named once. */
  function project(lon: number, lat: number) {
    return map.project([lon, lat]);
  }

  const districts = {
    layer: districtLayer,
    overlay,
    marks: markLayer,
    centreMark,
    project,
  };

  /**
   * Puts every drawn thing back where the current camera says it belongs,
   * without rebuilding any of it and without touching the camera itself —
   * `camera()` writes it, so calling that from a camera event would recurse.
   * Each element remembers the ground it stands on in its dataset.
   */
  function reproject() {
    if (disc === 0 || !placed) return;
    for (const el of markLayer.children as HTMLCollectionOf<HTMLElement>) {
      const lon = Number(el.dataset.lon);
      const lat = Number(el.dataset.lat);
      if (!Number.isFinite(lon) || !Number.isFinite(lat)) continue;
      const point = project(lon, lat);
      el.style.transform = `translate(${point.x}px, ${point.y}px)`;
      // Where the anchor now stands, for the clamp below: it works from these
      // numbers and the sizes `measureMarks` cached, so no frame measures a box.
      el.dataset.x = String(point.x);
      el.dataset.y = String(point.y);
      if (el === selectedEl) drawSpoke(overlay, spoke, spokeLine, { x: point.x, y: point.y });
    }
    // A mark that eased into the block's edge has to be held there for every
    // frame of the ease, not only for the paint that started it: the camera
    // moves under stationary ground, so which marks reach the edge changes
    // while nothing is rebuilt. It is arithmetic now, not a layout read (§4.3).
    clampMarks(markLayer, section, overlay);
    // The district names and the dots keep the ground they stand on, and that
    // is all a frame does to either of them: the district placement was solved
    // on the last settle and is carried as an offset, and a dot has nothing to
    // solve. Re-solving the names per frame is the offender §4.3 names.
    translateDistricts(districtLayer, project);
    translateBeyond(beyondLayer, project);
  }

  /**
   * The marks come from the standing derivation rather than from a derivation
   * of this function's own: the centre they were measured from and the clock
   * they were near-now'd against are `src/derived.ts`'s, so the disc and the
   * rail cannot disagree about the same instant (ADR-0016). Which is why this
   * takes no `centre` — it measures nothing.
   */
  function paintMarks(view: ViewState, dial: boolean) {
    markLayer.replaceChildren();
    selectedEl = null;
    let selectedAt: { x: number; y: number } | null = null;

    if (latest.derived) {
      const marks = marksFor(latest.derived.marked, markContext(view, dial));

      // Tags of one voice landing on one another read as a smudge, so they are
      // merged into one with a count. Dots are not clustered — a dot is already
      // the smallest thing on the disc — and the badges are merged after they
      // are drawn, because a badge's box is its line number's and a threshold
      // guessed from the widest line would merge badges that never touched.
      // The merge is in pixels, so it is redone here on every radius and centre
      // change rather than cached.
      const tags: Anchor<Extract<Mark, { kind: 'tag' }>>[] = [];
      for (const mark of marks) {
        if (mark.kind !== 'tag' || !mark.item.position) continue;
        const point = map.project([mark.item.position.lon, mark.item.position.lat]);
        tags.push({ item: mark, voice: mark.item.voice, x: point.x, y: point.y });
      }
      const clustered = new Map(clusterAnchors(tags).map((c) => [c.item.id, c]));

      // DOM order follows axis order, so the circle reads in the same order as
      // the list it is a picture of (§5.6).
      const badges: Badge[] = [];
      for (const mark of marks) {
        const position = mark.kind === 'dot' ? mark.position : mark.item.position;
        if (!position) continue;
        // A tag that was merged into an earlier one is simply not drawn.
        if (mark.kind === 'tag' && !clustered.has(mark.id)) continue;
        const point = map.project([position.lon, position.lat]);
        const cluster = mark.kind === 'tag' ? clustered.get(mark.id) : undefined;
        const holds = cluster?.members.map((m) => m.item) ?? (mark.kind === 'dot' ? [] : [mark.item]);
        // A merged tag reads selected when any of its members is, and the spoke
        // has to agree: the member that was merged away is never drawn, so
        // taking the spoke from `mark.selected` alone left a tag inverted with
        // no line to it — the second, non-colour signal of that very state.
        const selected =
          holds.length > 1 ? holds.some((m) => m.id === view.selected) : mark.kind !== 'dot' && mark.selected;
        const el = mark.kind === 'dot' ? dotElement() : markButton(mark.item, selected, mark.dimmed, holds);
        // The ground the mark stands on is the ANCHOR's, and the anchor has no
        // size: what is drawn is its child, which `clampMarks` may nudge by a
        // pixel or two at the block's edge while the ground stays exact.
        const anchor = document.createElement('div');
        anchor.className = 'circle__mark';
        // What KIND of mark this is, and whether it is the open one, live on
        // the anchor: the anchor's inline transform makes it a stacking
        // context, so a z-index inside it ranks its child against nothing and
        // the marks fall back to paint order. The ladder — dot, tag, badge,
        // the centre, a selected badge — only means anything up here.
        anchor.dataset.kind = mark.kind;
        anchor.classList.toggle('is-selected', selected);
        anchor.dataset.lon = String(position.lon);
        anchor.dataset.lat = String(position.lat);
        // Which ITEM this mark is for: the live half of a gesture asks the DOM
        // what it has already drawn before it creates anything (§4.1).
        anchor.dataset.item = mark.id;
        anchor.dataset.x = String(point.x);
        anchor.dataset.y = String(point.y);
        anchor.style.transform = `translate(${point.x}px, ${point.y}px)`;
        anchor.append(el);
        markLayer.append(anchor);
        if (mark.kind === 'badge')
          badges.push({ anchor, el, holds, dimmed: mark.dimmed, x: point.x, y: point.y });
        if (selected) {
          selectedEl = anchor;
          selectedAt = { x: point.x, y: point.y };
        }
      }

      const merged = mergeBadges(badges, view);
      if (merged) {
        selectedEl = merged.anchor;
        selectedAt = { x: merged.x, y: merged.y };
      }
    }

    measureMarks(markLayer);
    clampMarks(markLayer, section, overlay);
    drawSpoke(overlay, spoke, spokeLine, selectedAt);
  }

  // The live half of a gesture is `./live-marks.ts`'s: it hears
  // `src/state/gesture.ts` and never `derived.subscribe` (§1.1, rule 2).
  hearGesture({
    marks: markLayer,
    beyonds: beyondLayer,
    project,
    centre: () => ({ lat: map.getCenter().lat, lon: map.getCenter().lng }),
    placed: () => placed,
    axis: () => latest.derived,
    disc: () => disc,
  });
}
